import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Breadcrumbs, Dialog, Typography } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { Plus, Eye, Edit2, Trash2, Search, X } from "lucide-react";
import styles from "./questions.module.scss";
import { APIConstant } from "services/api-constant";
import Popup from "components/minor/popup/popup";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../../../../services/config';
const API_BASE = apiBaseUrl;
const ax = (token) => ({ Authorization: `Bearer ${token}` });

// ── Validation ──────────────────────────────────────────────────
const QuestionSchema = Yup.object().shape({
  question:        Yup.string().required("Question is required").min(3, "Min 3 chars"),
  expected_answer: Yup.string().nullable(),
  difficulty:      Yup.string().required("Difficulty is required"),
});

// ── Difficulty badge ────────────────────────────────────────────
const DiffBadge = ({ level }) => {
  const cls = {
    Hard:   styles.high,
    Medium: styles.medium,
    Easy:   styles.low,
    High:   styles.high,
    Low:    styles.low,
  };
  return <span className={`${styles.badge} ${cls[level] || styles.medium}`}>{level}</span>;
};

// ── Main component ──────────────────────────────────────────────
const QuestionsList = ({ token }) => {
  const { T } = useTheme();
  const { id } = useParams();
  const headers = ax(token);

  const [questions,  setQuestions]  = useState([]);
  const [tech,       setTech]       = useState({});
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [diffFilter, setDiffFilter] = useState("All");

  const [open,      setOpen]      = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [formInit,  setFormInit]  = useState({
    question:"", difficulty:"Medium", expected_answer:"", tags:""
  });

  const [delOpen, setDelOpen] = useState(false);
  const [delId,   setDelId]   = useState(null);

  // ── Fetch ──────────────────────────────────────────────────
  const fetchAll = async () => {
    try {
      setLoading(true);
      // Get technology name
      const techRes = await axios.get(APIConstant.getTechnologies, { headers });
      const foundTech = (techRes.data || []).find((t) => t.id == id);
      setTech(foundTech || {});

      // Fetch questions filtered by category = tech name
      const qRes = await axios.get(
        `${API_BASE}/interviews-v2/questions/list${foundTech?.name ? `?category=${encodeURIComponent(foundTech.name)}` : ""}`,
        { headers }
      );
      setQuestions(qRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Open modal ──────────────────────────────────────────────
  const openModal = (mode, record = null) => {
    setModalMode(mode);
    setFormInit(record ? {
      id:              record.id,
      question:        record.question        || "",
      difficulty:      record.difficulty      || "Medium",
      expected_answer: record.expected_answer || "",
      tags:            record.tags            || "",
    } : {
      question:"", difficulty:"Medium", expected_answer:"", tags:""
    });
    setOpen(true);
  };

  // ── Submit ──────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    const payload = {
      question:        values.question,
      category:        tech.name || "General",  // link to technology
      difficulty:      values.difficulty,
      expected_answer: values.expected_answer || null,
      tags:            values.tags            || null,
    };
    try {
      if (values.id) {
        await axios.put(`${API_BASE}/interviews-v2/questions/${values.id}`, payload, { headers });
      } else {
        await axios.post(`${API_BASE}/interviews-v2/questions/`, payload, { headers });
      }
      setOpen(false);
      fetchAll();
    } catch (e) { console.error(e); }
  };

  // ── Delete ──────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE}/interviews-v2/questions/${delId}`, { headers });
      setDelOpen(false);
      fetchAll();
    } catch (e) { console.error(e); }
  };

  // ── Filter ──────────────────────────────────────────────────
  const filtered = useMemo(() => questions.filter((q) => {
    const matchSearch = (q.question || "").toLowerCase().includes(search.toLowerCase()) ||
      (q.tags || "").toLowerCase().includes(search.toLowerCase());
    const matchDiff   = diffFilter === "All" || q.difficulty === diffFilter;
    return matchSearch && matchDiff;
  }), [questions, search, diffFilter]);

  const isView = modalMode === "view";
  const isEdit = modalMode === "edit";

  return (
    <div className={styles.pageRoot}>

      <Breadcrumbs className={styles.breadcrumb}>
        <Link to="/admin/questions" style={{ textDecoration:"none" }}>Questions</Link>
        <Typography style={{ fontSize:12, color:T.muted }}>{tech.name || "List"}</Typography>
      </Breadcrumbs>

      <div className={styles.questionsWrapper}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <h3>{tech.name || "Questions"}</h3>
            <p>{questions.length} total questions</p>
          </div>
          <button className={styles.btnPrimary} onClick={() => openModal("add")}>
            <Plus size={14}/> Add Question
          </button>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.searchRow}>
            <div style={{ position:"relative", flex:1 }}>
              <Search size={14} style={{ position:"absolute", left:12, top:"50%",
                transform:"translateY(-50%)", color:T.dim }}/>
              <input className={styles.searchInput} style={{ paddingLeft:34 }}
                placeholder="Search questions or tags…"
                value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <select className={styles.filterSelect} value={diffFilter}
              onChange={(e) => setDiffFilter(e.target.value)}>
              <option value="All">All Levels</option>
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>

          {loading ? (
            <div className={styles.loader}><div className={styles.spinner}/>Loading…</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.darkTable}>
                <thead>
                  <tr>
                    <th style={{ width:40 }}>#</th>
                    <th>Question</th>
                    <th>Tags</th>
                    <th>Difficulty</th>
                    <th style={{ width:110 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr className={styles.emptyRow}>
                      <td colSpan={5}>
                        {search || diffFilter !== "All"
                          ? "No questions match your filters"
                          : "No questions yet — add one!"}
                      </td>
                    </tr>
                  ) : filtered.map((q, i) => (
                    <tr key={q.id}>
                      <td style={{ color:T.dim, fontFamily:"JetBrains Mono,monospace",
                        fontSize:12 }}>{i + 1}</td>
                      <td>
                        <div className={styles.qText}>{q.question}</div>
                        {q.expected_answer && (
                          <div className={styles.qAnswer}>
                            Answer: {q.expected_answer.slice(0, 80)}{q.expected_answer.length > 80 ? "…" : ""}
                          </div>
                        )}
                      </td>
                      <td>
                        {q.tags
                          ? q.tags.split(",").map((t) => t.trim()).filter(Boolean).map((tag) => (
                            <span key={tag} style={{ fontSize:10.5, padding:"1px 6px",
                              borderRadius:100, background:`${T.cyan}14`,
                              color:T.muted, border:`1px solid ${T.cyan}1F`,
                              marginRight:4, display:"inline-block", marginBottom:2 }}>
                              #{tag}
                            </span>
                          ))
                          : <span style={{ color:T.dim }}>—</span>
                        }
                      </td>
                      <td><DiffBadge level={q.difficulty}/></td>
                      <td>
                        <div className={styles.actions}>
                          <button className={`${styles.iconBtn} ${styles.viewBtn}`}
                            onClick={() => openModal("view", q)} title="View">
                            <Eye size={13}/>
                          </button>
                          <button className={`${styles.iconBtn} ${styles.editBtn}`}
                            onClick={() => openModal("edit", q)} title="Edit">
                            <Edit2 size={13}/>
                          </button>
                          <button className={`${styles.iconBtn} ${styles.delBtn}`}
                            onClick={() => { setDelId(q.id); setDelOpen(true); }} title="Delete">
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit / View Modal ── */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <div className={styles.formModal}>
          <div className={styles.formModalHeader}>
            <h3>
              {modalMode === "add" ? "Add" : modalMode === "edit" ? "Edit" : "View"} Question
            </h3>
            <button style={{ background:"none", border:"none",
              cursor:"pointer", color:T.muted }}
              onClick={() => setOpen(false)}>
              <X size={16}/>
            </button>
          </div>

          <div className={styles.formModalBody}>
            <Formik
              enableReinitialize
              initialValues={formInit}
              validationSchema={isView ? undefined : QuestionSchema}
              onSubmit={handleSubmit}
            >
              {({ values, handleChange, handleSubmit, errors, touched }) => (
                <form onSubmit={handleSubmit}>

                  {/* Technology read-only */}
                  <div className={styles.formRow}>
                    <label>Technology</label>
                    <div className={styles.formField}>
                      <span className={styles.techTag}>{tech.name || "—"}</span>
                    </div>
                  </div>

                  {/* Question */}
                  <div className={styles.formRow}>
                    <label>Question *</label>
                    <div className={styles.formField}>
                      <textarea name="question" className={styles.darkInput}
                        placeholder="Enter interview question…"
                        rows={3} value={values.question}
                        onChange={handleChange} disabled={isView}/>
                      {errors.question && touched.question && (
                        <p className={styles.formError}>{errors.question}</p>
                      )}
                    </div>
                  </div>

                  {/* Difficulty */}
                  <div className={styles.formRow}>
                    <label>Difficulty</label>
                    <div className={styles.formField}>
                      <select name="difficulty" className={styles.darkSelect}
                        value={values.difficulty}
                        onChange={handleChange} disabled={isView}>
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </select>
                    </div>
                  </div>

                  {/* Expected answer */}
                  <div className={styles.formRow}>
                    <label>Expected Answer</label>
                    <div className={styles.formField}>
                      <textarea name="expected_answer" className={styles.darkInput}
                        placeholder="Expected / ideal answer…"
                        rows={4} value={values.expected_answer}
                        onChange={handleChange} disabled={isView}/>
                    </div>
                  </div>

                  {/* Tags */}
                  {!isView && (
                    <div className={styles.formRow}>
                      <label>Tags</label>
                      <div className={styles.formField}>
                        <input name="tags" className={styles.darkInput}
                          placeholder="e.g. javascript, async, es6"
                          value={values.tags} onChange={handleChange}/>
                        <p style={{ fontSize:11, color:T.dim, marginTop:3 }}>
                          Comma-separated tags
                        </p>
                      </div>
                    </div>
                  )}

                  {!isView && (
                    <div className={styles.formActions}>
                      <button type="button" className={styles.btnSecondary}
                        onClick={() => setOpen(false)}>Cancel</button>
                      <button type="submit" className={styles.btnPrimary}>
                        {isEdit ? "Update" : "Save"}
                      </button>
                    </div>
                  )}
                </form>
              )}
            </Formik>
          </div>
        </div>
      </Dialog>

      <Popup
        open={delOpen}
        message="Are you sure you want to delete this question?"
        handleClose={() => setDelOpen(false)}
        handleConfirm={handleDelete}
      />
    </div>
  );
};

export default QuestionsList;